export const ROUTES = {
  main: '/',
  home: '/home',

  // Auth
  login: '/login',
  register: '/register',
  verifyEmail: '/verify-email',
  forgotPassword: '/forgot-password',

  // Main
  profile: '/dashboard/profile',

  // Dashboard (Admin only)
  dashboard: '/dashboard',
  dashboardProviders: '/dashboard/providers',
  dashboardProviderDetails: '/dashboard/providers/:id',
  dashboardProvidersNew: '/dashboard/providers/new',
  dashboardProvidersEdit: '/dashboard/providers/:id/edit',
  dashboardServices: '/dashboard/services',
  dashboardAttributes: '/dashboard/attributes',
  dashboardPricing: '/dashboard/pricing',
  dashboardCategories: '/dashboard/categories',
  dashboardTokenUsage: '/dashboard/token-usage',
  dashboardProviderStats: '/dashboard/provider-stats',
  dashboardChat: '/dashboard/chat',
  dashboardChatSession: '/dashboard/chat/:sessionId',
  dashboardGenerateInvitation: '/dashboard/generate-invitation',
  dashboardGeneratePresentation: '/dashboard/generate-presentation',
  dashboardInvitations: '/dashboard/invitations',
  dashboardInvitationDetail: '/dashboard/invitations/:id',
  dashboardInvitationEdit: '/dashboard/invitations/:id/edit',
  dashboardPresentations: '/dashboard/presentations',
  dashboardPresentationDetail: '/dashboard/presentations/:id',
  dashboardPresentationEdit: '/dashboard/presentations/:id/edit',

  dashboardGenerateRenders: '/dashboard/generate-renders',
  dashboardRenders: '/dashboard/renders',
  dashboardRenderDetail: '/dashboard/renders/:id',

  // Deals
  dashboardDeals: '/dashboard/deals',
  dashboardDealDetail: '/dashboard/deals/:id',

  // Admin-only
  dashboardUsers: '/dashboard/users',
  dashboardAdminActions: '/dashboard/admin-actions',
  dashboardNotifications: '/dashboard/notifications',

  // Unassigned users
  dashboardWaiting: '/dashboard/waiting',

  // Provider role landing
  dashboardMyProviders: '/dashboard/my-providers',

  // Legal
  privacy: '/privacy',
  terms: '/terms',

  // External (no auth)
  externalProviderSubmit: '/submit-provider',

  notFound: '*',
};

export const API_ROUTES = {
  // Auth
  login: '/auth/login/',
  logout: '/auth/logout/',
  refresh: '/auth/token/refresh/',
  signup: '/auth/registration/',
  resendEmail: '/resend-email-confirmation/',
  confirmEmail: '/auth/registration/account-confirm-email/',
  changePassword: '/auth/password/change/',
  resetPassword: '/auth/password/reset/',
  resetPasswordConfirm: '/auth/password/reset/confirm/',

  // Other
  profile: '/auth/user/',

  // Providers API
  providers: '/api/providers/providers/',
  providerDashboardStats: '/api/providers/providers/dashboard-stats/',
  providersLight: '/api/providers/providers/light/',
  providerCreators: '/api/providers/providers/creators/',
  uploadProviderDocument: '/api/providers/upload-document/',
  analyzeProviderDocument: '/api/providers/analyze-document/',
  analyzeBatchDocuments: '/api/providers/analyze-documents-batch/',
  uploadProviderImage: '/api/providers/upload-image/',
  services: '/api/providers/catalog/services/',
  servicesLight: '/api/providers/catalog/services/light/',
  categories: '/api/providers/catalog/categories/',
  attributes: '/api/providers/catalog/attributes/',
  pricingUnits: '/api/providers/catalog/pricing-units/',
  offerings: '/api/providers/offerings/',
  tokenUsage: '/api/providers/token-usage/',
  presentations: '/api/providers/presentations/',
  uploadPresentationImage: '/api/providers/presentations/upload-image/',
  proxyPresentationImage: '/api/providers/presentations/proxy-image/',

  // Wizard & Editor
  plantillas: '/api/providers/plantillas/',
  themes: '/api/providers/themes/',
  wizardGenerate: '/api/providers/presentations/wizard-generate/',
  generatingStatus: '/api/providers/presentations/generating-status/',
  editorOnlyofficeConfig: '/api/providers/editor/onlyoffice/config/',
  driveUpload: '/api/providers/editor/drive/upload/',
  drivePull: '/api/providers/editor/drive/pull/',

  // External (no auth required)
  externalAnalyzeDocument: '/api/providers/external/analyze-document/',
  externalAnalyzeBatch: '/api/providers/external/analyze-documents-batch/',
  externalSubmitProvider: '/api/providers/external/submit-provider/',

  // Google OAuth
  googleConnect: '/auth/google/connect/',
  googleAuth: '/auth/google/',

  // User Management (admin only)
  users: '/api/users/',

  // Provider Users
  providerUsers: '/api/providers/providers/', // append :id/users/

  // Audit Log
  auditLog: '/api/audit/',

  // Deals
  deals: '/api/deals/',

  // Notifications (admin only)
  notificationNumbers: '/api/notifications/numbers/',
  notificationLogs: '/api/notifications/logs/',
  notificationTest: '/api/notifications/test/',
};

// ── Full-bleed layout detection ──────────────────────────────────
// Routes that need the full-bleed (no padding) content area.
// Supports exact string matches and regex patterns.
export const FULL_BLEED_PATTERNS: Array<string | RegExp> = [
  ROUTES.dashboardChat,
  /\/chat\/[0-9a-f-]+$/,
  ROUTES.dashboardProvidersNew,
  /\/providers\/[0-9a-f-]+\/edit$/,
  /\/presentations\/[0-9a-f-]+\/edit$/,
  /\/deals\/[0-9a-f-]+$/,
  ROUTES.dashboardGeneratePresentation,
  ROUTES.dashboardGenerateInvitation,
  ROUTES.dashboardGenerateRenders,
];

/**
 * Returns true if the given pathname should use the full-bleed (no-padding) layout.
 * Used by DashboardLayout to switch between `contentArea` and `contentAreaChat`.
 */
export function isFullBleedRoute(pathname: string): boolean {
  return FULL_BLEED_PATTERNS.some((pattern) =>
    typeof pattern === 'string' ? pathname === pattern : pattern.test(pathname),
  );
}

// Only these paths dont require to send a token
// See /lib/axios.ts
export const NO_AUTH_REQUIRED_API_ROUTES = [
  API_ROUTES.login,
  API_ROUTES.logout,
  API_ROUTES.refresh,
  API_ROUTES.signup,
  API_ROUTES.resendEmail,
  API_ROUTES.confirmEmail,
  API_ROUTES.resetPassword,
  API_ROUTES.resetPasswordConfirm,
  API_ROUTES.externalAnalyzeDocument,
  API_ROUTES.externalAnalyzeBatch,
  API_ROUTES.externalSubmitProvider,
  API_ROUTES.googleAuth,
];
